import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useSession, useToast } from '../lib/store';
import type { ShopItem, Wallet } from '../lib/types';
import { Button, Cost, ErrorState, LoadingGrid, Panel } from '../components/ui';

interface ShopResponse {
  items: ShopItem[];
  wallet: Wallet;
  /** The server's own cap, so this page never offers an amount it will refuse. */
  maxPerPurchase: number;
  upgrades: {
    hatcherySlots: number;
    maxHatcherySlots: number;
    nextHatcherySlotCost: number;
  };
}

const ITEM_ART: Record<string, string> = {
  blanket: '▧',
  candy: '◆',
  bell: '◍',
  'evolution-link': '⟳',
  generic: '◈',
};

export function ShopPage() {
  const { me, setWallet, refresh } = useSession();
  const { push } = useToast();
  const state = useAsync<ShopResponse>(() => api.get('/shop'));
  const [busy, setBusy] = useState<string | null>(null);
  /** How many of each item the shopper has dialled up. Absent means one. */
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  async function buy(item: ShopItem, quantity: number) {
    setBusy(item.key);
    try {
      const result = await api.post<{ quantity: number; totalPrice: number; wallet: Wallet }>(
        '/shop/buy',
        { itemKey: item.key, quantity },
      );
      setWallet(result.wallet);
      // The server's numbers, not the ones this page assumed. Prices are the
      // server's to decide, so the receipt is too.
      push(
        'reward',
        result.quantity > 1
          ? `${result.quantity} x ${item.name} added to your bag — ${result.totalPrice.toLocaleString()} spent.`
          : `${item.name} added to your bag.`,
        '◆',
      );
      // Back to one, so the next click on a neighbouring card is not a
      // surprise purchase of ten at whatever the last card was set to.
      setQuantities((current) => ({ ...current, [item.key]: 1 }));
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not buy that.');
    } finally {
      setBusy(null);
    }
  }

  async function upgrade(kind: 'hatchery') {
    setBusy(kind);
    try {
      const result = await api.post<{ slots: number; wallet: Wallet }>('/shop/upgrade', { kind });
      setWallet(result.wallet);
      push('reward', `Incubator expanded to ${result.slots} slots!`, '★');
      state.reload();
      void refresh();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not upgrade.');
    } finally {
      setBusy(null);
    }
  }

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;

  const upgrades = state.data?.upgrades;
  const coins = me?.wallet.pokeCoins ?? 0;

  const grouped = (state.data?.items ?? []).reduce<Record<string, ShopItem[]>>((acc, item) => {
    (acc[item.category] ??= []).push(item);
    return acc;
  }, {});

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Shop</h1>
          <div className="page-head__sub">
            Everything here is earned in game. There is no real-money currency in PokéGrove.
          </div>
        </div>
      </div>

      {upgrades && (
        <Panel title="★ Permanent upgrades">
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
            <UpgradeCard
              title="Incubator slot"
              description="Warm one more egg at a time. The single biggest boost to how fast your collection grows."
              current={upgrades.hatcherySlots}
              max={upgrades.maxHatcherySlots}
              cost={upgrades.nextHatcherySlotCost}
              affordable={coins >= upgrades.nextHatcherySlotCost}
              busy={busy === 'hatchery'}
              onBuy={() => void upgrade('hatchery')}
            />
            <div className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
              <div className="stack-sm">
                <span className="mono tiny">Grove space</span>
                <p className="tiny muted">
                  Room for more Pokémon and decorations isn't bought — your Grove grows on its own as you
                  hatch, help other trainers, and receive visitors.
                </p>
                <Link className="btn btn--sm btn--go" to="/grove">
                  🌱 Visit your Grove
                </Link>
              </div>
            </div>
          </div>
        </Panel>
      )}

      {state.loading ? (
        <LoadingGrid count={6} height={180} />
      ) : (
        Object.entries(grouped).map(([category, items]) => (
          <Panel
            key={category}
            title={
              category === 'EVOLUTION'
                ? '⟳ Evolution items'
                : category === 'DECORATION'
                  ? '🌿 Grove decorations'
                  : '◆ Consumables'
            }
          >
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))' }}>
              {items.map((item) => {
                const balance =
                  item.currency === 'pokeCoins'
                    ? me?.wallet.pokeCoins ?? 0
                    : me?.wallet.friendshipTokens ?? 0;
                return (
                  <div key={item.key} className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
                    <div className="stack-sm">
                      <div className="row">
                        {item.category === 'DECORATION' ? (
                          // Show the actual scenery, scaled down — a shopper
                          // should see what they're buying, not a glyph.
                          <span
                            style={{ width: 40, height: 44, display: 'grid', placeItems: 'end center' }}
                            aria-hidden
                          >
                            <span className={`decor__art art-${item.art}`} style={{ transform: 'scale(.42)' }} />
                          </span>
                        ) : (
                          <span style={{ fontSize: '1.6rem', color: 'var(--ink-soft)' }} aria-hidden>
                            {ITEM_ART[item.art] ?? ITEM_ART.generic}
                          </span>
                        )}
                        <div style={{ flex: 1 }}>
                          <div className="mono tiny">{item.name}</div>
                          {item.owned > 0 && <div className="tiny muted">You have {item.owned}</div>}
                        </div>
                      </div>
                      <p className="tiny muted">{item.description}</p>
                      <BuyControls
                        item={item}
                        balance={balance}
                        max={state.data?.maxPerPurchase ?? 1}
                        quantity={quantities[item.key] ?? 1}
                        busy={busy === item.key}
                        onQuantity={(next) =>
                          setQuantities((current) => ({ ...current, [item.key]: next }))
                        }
                        onBuy={(quantity) => void buy(item, quantity)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Panel>
        ))
      )}
    </div>
  );
}

/** How big a jump the stepper takes once it is past one. */
const QUANTITY_STEP = 5;

/**
 * The amounts the stepper stops at: 1, then 5, 10, 15 … up to the ceiling.
 *
 * A ladder rather than ±1 because the complaint this whole feature answers was
 * "I can only buy one at a time" — and a stepper that needs ten taps to reach
 * ten has not really fixed that. One is the first rung because buying a single
 * Rare Candy is still the common case.
 *
 * The ceiling gets its own rung when it is not already a multiple of the step.
 * With 13 affordable the rungs are 1, 5, 10, 13: refusing to let somebody buy
 * the thirteenth because thirteen is not a round number would be a worse shop
 * than one that never offered the choice.
 */
function quantityLadder(ceiling: number): number[] {
  const rungs = [1];
  for (let value = QUANTITY_STEP; value <= ceiling; value += QUANTITY_STEP) rungs.push(value);
  if (rungs[rungs.length - 1]! < ceiling) rungs.push(ceiling);
  return rungs;
}

/**
 * Price, quantity and the buy button for one shelf item.
 *
 * Players were buying Warm Blankets one click at a time, which is the item this
 * game is most likely to want ten of. The stepper walks `quantityLadder` — 1,
 * 5, 10, 15, 20 — so reaching twenty is four taps rather than nineteen.
 *
 * Two rules it keeps:
 *
 *   - **The ceiling is real.** `+` stops at whatever the player can actually
 *     afford, and at the server's own `maxPerPurchase`. A shop that lets you
 *     dial up a number it will then refuse is a shop that wastes a click to
 *     tell you something it already knew.
 *   - **The total is on the button.** The number being spent is the thing worth
 *     confirming, and it belongs where the finger is going rather than in a
 *     line above it.
 */
function BuyControls({
  item,
  balance,
  max,
  quantity,
  busy,
  onQuantity,
  onBuy,
}: {
  item: ShopItem;
  balance: number;
  max: number;
  quantity: number;
  busy: boolean;
  onQuantity: (next: number) => void;
  onBuy: (quantity: number) => void;
}) {
  const affordableCount = item.price > 0 ? Math.floor(balance / item.price) : max;
  const ceiling = Math.max(1, Math.min(max, affordableCount));
  const rungs = quantityLadder(ceiling);

  // Snapped on render rather than only on click: a purchase that empties the
  // wallet leaves a stale number dialled up, and it should correct itself
  // rather than wait to be refused. Snapping *down* is the safe direction —
  // never quietly raise what somebody is about to spend.
  const wanted = Math.max(1, quantity);
  let index = 0;
  for (let i = 0; i < rungs.length; i += 1) {
    if (rungs[i]! <= wanted) index = i;
  }
  const chosen = rungs[index]!;
  const total = item.price * chosen;
  const canAfford = balance >= total;
  const canBuyMore = rungs.length > 1;

  return (
    <div className="stack-sm">
      <div className="row row--between">
        <Cost amount={item.price} currency={item.currency} />
        {canBuyMore && (
          <div className="row shop-qty" style={{ gap: 3 }}>
            <Button
              size="sm"
              variant="ghost"
              aria-label={`Fewer ${item.name}`}
              disabled={busy || index <= 0}
              onClick={() => onQuantity(rungs[index - 1]!)}
            >
              −
            </Button>
            <span className="mono tiny shop-qty__value" aria-live="polite">
              {chosen}
            </span>
            <Button
              size="sm"
              variant="ghost"
              aria-label={`More ${item.name}`}
              disabled={busy || index >= rungs.length - 1}
              onClick={() => onQuantity(rungs[index + 1]!)}
            >
              +
            </Button>
          </div>
        )}
      </div>

      <Button
        size="sm"
        block
        variant={canAfford ? 'primary' : 'default'}
        disabled={!canAfford || busy}
        loading={busy}
        onClick={() => onBuy(chosen)}
      >
        {!canAfford
          ? 'Too expensive'
          : chosen > 1
            ? `Buy ${chosen} · ${total.toLocaleString()}`
            : 'Buy'}
      </Button>
    </div>
  );
}

function UpgradeCard({
  title,
  description,
  current,
  max,
  cost,
  affordable,
  busy,
  onBuy,
}: {
  title: string;
  description: string;
  current: number;
  max: number;
  cost: number;
  affordable: boolean;
  busy: boolean;
  onBuy: () => void;
}) {
  const maxed = current >= max;
  return (
    <div className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
      <div className="stack-sm">
        <div className="row row--between">
          <span className="mono tiny">{title}</span>
          <span className="chip">
            {current} / {max}
          </span>
        </div>
        <p className="tiny muted">{description}</p>
        <div className="row row--between">
          {maxed ? <span className="tiny mono" style={{ color: 'var(--leaf-dark)' }}>MAXED</span> : <Cost amount={cost} currency="pokeCoins" />}
          <Button size="sm" variant="gold" disabled={maxed || !affordable || busy} loading={busy} onClick={onBuy}>
            {maxed ? 'Complete' : affordable ? 'Unlock' : 'Not enough'}
          </Button>
        </div>
      </div>
    </div>
  );
}
